import { MAX_CREATIVE_PREVIEWS } from "@nexus/contracts";
import type {
  InsightsCreativePreview,
  PreflightPlatformCode,
  PreflightReportSortField,
  PreflightReportSortOrder,
} from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import { downloadBlobAs } from "../lib/download";
import { serializeMarketFilter } from "../lib/market-filter";
import { API_REQUEST_TIMEOUT_LONG_MS } from "@/lib/query-constants";
import type { PreflightMarketCatalogueEntry } from "@nexus/contracts";
import type {
  AddPreflightMarketInput,
  CreatePreflightBrandBody,
  CreateReportInput,
  CreateReportResult,
  PreflightBrand,
  PreflightInsights,
  PreflightMarket,
  PreflightMarketsList,
  PreflightReportDetail,
  PreflightReportListPage,
  PreflightReportStatusResult,
  PreflightReportSummary,
  RetryReportResult,
} from "../types/preflight.types";

const REPORTS_PATH = "/preflight/reports";
const MARKETS_PATH = "/preflight/markets";
const MARKET_CATALOGUE_PATH = "/preflight/market-catalogue";
const BRANDS_PATH = "/preflight/brands";
const INSIGHTS_PATH = "/preflight/insights";
const CREATIVE_PREVIEWS_PATH = "/preflight/creative-previews";

/** Create a report from the assembled draft. Returns only `{ reportId }` (status defaults to QUEUED). */
export async function createReport(
  input: CreateReportInput,
): Promise<CreateReportResult> {
  const { data } = await authFetch<CreateReportResult>(REPORTS_PATH, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export interface ListReportsParams {
  cursor?: string;
  limit?: number;
  search?: string;
  platform?: PreflightPlatformCode;
  /** Omitted in all-markets scope. A string[] is an OR membership filter. */
  market?: string | readonly string[];
  /** Omitted for the server's default newest-first ordering. */
  sortBy?: PreflightReportSortField;
  sortOrder?: PreflightReportSortOrder;
  /** Keep only the reports the signed-in user has starred. */
  favoritesOnly?: boolean;
}

/**
 * Cursor-paginated reports list with optional server-side search, platform and
 * market filters, and server-side ordering.
 */
export async function listReports(
  params: ListReportsParams = {},
): Promise<PreflightReportListPage> {
  const query = new URLSearchParams();
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit != null) query.set("limit", String(params.limit));
  if (params.search) query.set("search", params.search);
  if (params.platform) query.set("platform", params.platform);
  const market = serializeMarketFilter(params.market);
  if (market) query.set("market", market);
  if (params.sortBy) query.set("sortBy", params.sortBy);
  if (params.sortOrder) query.set("sortOrder", params.sortOrder);
  if (params.favoritesOnly) query.set("favoritesOnly", "true");
  const qs = query.toString();
  const { data } = await authFetch<PreflightReportListPage>(
    `${REPORTS_PATH}${qs ? `?${qs}` : ""}`,
  );
  return data;
}

/** Full report detail incl. per-asset projected features + signed thumbnail URLs. */
export async function getReport(id: string): Promise<PreflightReportDetail> {
  const { data } = await authFetch<PreflightReportDetail>(
    `${REPORTS_PATH}/${id}`,
  );
  return data;
}

/**
 * Lightweight status read-through (cache-backed). Carries enough (status, score,
 * band, counts) to flip a row in place without always invalidating the detail.
 */
export async function getReportStatus(
  id: string,
): Promise<PreflightReportStatusResult> {
  const { data } = await authFetch<PreflightReportStatusResult>(
    `${REPORTS_PATH}/${id}/status`,
  );
  return data;
}

/** Rename a report. Returns the updated summary. */
export async function renameReport(
  id: string,
  name: string,
): Promise<PreflightReportSummary> {
  const { data } = await authFetch<PreflightReportSummary>(
    `${REPORTS_PATH}/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify({ name }),
    },
  );
  return data;
}

/**
 * Reassign a report to one or more tenant markets. Uses the same PATCH +
 * MANAGE permission as rename so anyone who can rename can also change market.
 */
export async function updateReportMarket(
  id: string,
  marketIds: string[],
): Promise<PreflightReportSummary> {
  const { data } = await authFetch<PreflightReportSummary>(
    `${REPORTS_PATH}/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify({ marketIds }),
    },
  );
  return data;
}

/** Star or unstar a report for the signed-in user. Returns the updated summary. */
export async function setReportFavorite(
  id: string,
  isFavorite: boolean,
): Promise<PreflightReportSummary> {
  const { data } = await authFetch<PreflightReportSummary>(
    `${REPORTS_PATH}/${id}/favorite`,
    {
      method: "PATCH",
      body: JSON.stringify({ isFavorite }),
    },
  );
  return data;
}

/** Soft-delete a report (204 No Content). */
export async function deleteReport(id: string): Promise<void> {
  await authFetch(`${REPORTS_PATH}/${id}`, { method: "DELETE", rawResponse: true });
}

/**
 * Re-enqueue a failed/partial report for analysis. With `assetId`, only that
 * failed creative re-runs; without it, every failed creative does.
 */
export async function retryReport(
  id: string,
  assetId?: string,
): Promise<RetryReportResult> {
  const { data } = await authFetch<RetryReportResult>(
    `${REPORTS_PATH}/${id}/retry`,
    assetId
      ? { method: "POST", body: JSON.stringify({ assetId }) }
      : { method: "POST" },
  );
  return data;
}

/** The tenant's market list plus the unique live-report count (All Markets). */
export async function listMarkets(): Promise<PreflightMarketsList> {
  const { data } = await authFetch<PreflightMarketsList>(MARKETS_PATH);
  return data;
}

/** Active global catalogue identities for the Pre Flight picker. */
export async function listMarketCatalogue(): Promise<PreflightMarketCatalogueEntry[]> {
  const { data } = await authFetch<PreflightMarketCatalogueEntry[]>(MARKET_CATALOGUE_PATH);
  return data;
}

/**
 * Add a market by naming a catalogue entry. The server resolves the stored name
 * from the entry, so no name travels as an identity (R2, KTD2).
 */
export async function createMarket(
  input: AddPreflightMarketInput,
): Promise<PreflightMarket> {
  const { data } = await authFetch<PreflightMarket>(MARKETS_PATH, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

/** Remove an unused market (204 No Content); an in-use market returns 409. */
export async function deleteMarket(id: string): Promise<void> {
  await authFetch(`${MARKETS_PATH}/${id}`, { method: "DELETE", rawResponse: true });
}

/** The tenant's brand names, alphabetical. */
export async function listBrands(): Promise<PreflightBrand[]> {
  const { data } = await authFetch<PreflightBrand[]>(BRANDS_PATH);
  return data;
}

/** Add a brand name; a name already in the list (any case) returns 409. */
export async function createBrand(input: CreatePreflightBrandBody): Promise<PreflightBrand> {
  const { data } = await authFetch<PreflightBrand>(BRANDS_PATH, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

/** Rename a brand; a name another brand holds (any case) returns 409. Reports keep their name. */
export async function renameBrand(id: string, input: CreatePreflightBrandBody): Promise<PreflightBrand> {
  const { data } = await authFetch<PreflightBrand>(`${BRANDS_PATH}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return data;
}

/** Remove a brand name (204 No Content). Reports keep the brand they were scored for. */
export async function deleteBrand(id: string): Promise<void> {
  await authFetch(`${BRANDS_PATH}/${id}`, { method: "DELETE", rawResponse: true });
}

export interface GetInsightsParams {
  /** Omitted for the all-markets aggregate. A string[] is an OR membership filter. */
  market?: string | readonly string[];
}

/** Cached aggregate over every scored creative in the selected market scope. */
export async function getInsights(
  params: GetInsightsParams = {},
): Promise<PreflightInsights> {
  const market = serializeMarketFilter(params.market);
  const qs = market ? `?market=${encodeURIComponent(market)}` : "";
  const { data } = await authFetch<PreflightInsights>(`${INSIGHTS_PATH}${qs}`);
  return data;
}

/**
 * Signed thumbnails for specific creatives. Read URLs are short-lived, so the aggregate
 * cannot carry them: the rail asks for the creatives one open panel shows.
 */
export async function getCreativePreviews(
  assetIds: readonly string[],
): Promise<InsightsCreativePreview[]> {
  const ids = assetIds.slice(0, MAX_CREATIVE_PREVIEWS).join(",");
  const { data } = await authFetch<InsightsCreativePreview[]>(
    `${CREATIVE_PREVIEWS_PATH}?ids=${encodeURIComponent(ids)}`,
  );
  return data;
}

/**
 * Download the server-rendered PDF for a report. Reads Content-Disposition
 * to derive the filename, then triggers a browser download.
 */
export async function downloadReportPdf(id: string): Promise<void> {
  const res = await authFetch(`${REPORTS_PATH}/${id}/export.pdf`, {
    rawResponse: true,
    timeoutMs: API_REQUEST_TIMEOUT_LONG_MS,
  });

  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? `report-${id}.pdf`;

  downloadBlobAs(await res.blob(), filename);
}
